import { useEffect, useState } from 'react'
import { fetchLeadScore, type LeadScoreResponse } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'

interface Props { range: { from: number; to: number } }

export function LeadScoreHistogram({ range }: Props) {
  const { variantParam, audienceParam } = useFlowFilter()
  const [data, setData] = useState<LeadScoreResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchLeadScore(range, variantParam, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, audienceParam])

  if (error) return <div className="card card-empty">Kunne ikke hente lead score</div>
  if (!data) return <div className="card card-skeleton" aria-busy />
  if (data.total === 0) return <div className="card card-empty"><div className="card-title">Lead score</div><div className="muted">Ingen leads i perioden</div></div>

  const max = Math.max(1, ...data.buckets.map((b) => b.n))
  const highQualityShare = data.buckets.filter((b) => b.score >= 4).reduce((sum, b) => sum + b.n, 0) / data.total

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Lead score fordeling</div>
        <div className="card-subtitle">Skala 1–5 baseret på præmie, alder og forsikringsstatus</div>
      </div>
      <div className="leadscore-stats">
        <div className="leadscore-stat">
          <div className="stat-label">Gennemsnit</div>
          <div className="stat-value">{data.avg !== null ? data.avg.toFixed(1) : '—'}</div>
        </div>
        <div className="leadscore-stat">
          <div className="stat-label">Median</div>
          <div className="stat-value">{data.median !== null ? data.median.toFixed(1) : '—'}</div>
        </div>
        <div className="leadscore-stat">
          <div className="stat-label">High-quality (4–5)</div>
          <div className="stat-value">{(highQualityShare * 100).toFixed(0)}%</div>
        </div>
      </div>
      <div className="leadscore-bars">
        {data.buckets.map((b) => {
          const pct = b.n / max
          const tone = b.score >= 4 ? 'good' : b.score >= 3 ? 'mid' : 'low'
          return (
            <div key={b.score} className={`leadscore-bar leadscore-bar-${tone}`}>
              <div className="leadscore-bar-track">
                <div className="leadscore-bar-fill" style={{ height: `${pct * 100}%` }} />
              </div>
              <div className="leadscore-bar-count">{b.n}</div>
              <div className="leadscore-bar-label">{b.score}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
